import { Close } from "@mui/icons-material";
import { accordionClasses, Box, Button, IconButton, List, Typography, useTheme } from "@mui/material";
import { ObjectId } from "@novorender/api";
import { useDrag } from "@use-gesture/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { Accordion, AccordionDetails, AccordionSummary, Divider, ScrollBox, Tooltip } from "components";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import InfoPopup from "features/deviations/components/infoPopup";
import { renderActions, selectStamp, StampKind } from "features/render";
import { getFileNameFromPath, getFilePathFromObjectPath, getPropertyDisplayName, isUrl } from "utils/objectData";
import { getObjectData } from "utils/search";

import { ResizeHandle } from "./resizeHandle";
import { selectShowPropertiesStamp, selectStarredProperties } from "./slice";
import { useSelectObject } from "./useSelectObject";

type PropertyGroup = {
    name: string;
    objectId: ObjectId;
    properties: [key: string, value: string][];
};

export function PropertiesStamp() {
    const {
        state: { db, view },
    } = useExplorerGlobals();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const stamp = useAppSelector(selectStamp);
    const starred = useAppSelector(selectStarredProperties);
    const enabled = useAppSelector(selectShowPropertiesStamp);
    const [propertyGroups, setPropertyGroups] = useState<PropertyGroup[]>();
    const show = stamp?.kind === StampKind.Properties && enabled;
    const selectObject = useSelectObject();

    const resizing = useRef(false);
    const lastMovementX = useRef(0);
    const [propertyNameWidth, setPropertyNameWidth] = useState(95);
    const bindResizeHandlers = useDrag(({ target, dragging, movement: [movementX] }) => {
        if (!(target instanceof HTMLDivElement) || !target.dataset.resizeHandle) {
            return;
        }

        resizing.current = true;

        const toMove = movementX - lastMovementX.current;
        lastMovementX.current = dragging ? movementX : 0;

        setPropertyNameWidth((state) => (state + toMove < 25 ? 25 : state + toMove > 300 ? 300 : state + toMove));
    });

    useEffect(() => {
        if (!show) {
            setPropertyGroups(undefined);
            return;
        }

        const starredLc = new Set(Object.keys(starred).map((key) => key.toLowerCase()));
        const groups = stamp.properties
            .map((properties, idx) => {
                return {
                    name: properties[0][1],
                    objectId: stamp.objectIds[idx],
                    properties: properties.filter(([key, value]) => {
                        if (!value) {
                            return false;
                        }
                        const keyLc = key.toLowerCase();
                        if (!starredLc.has(keyLc)) {
                            return false;
                        }
                        const isPath = keyLc === "path";
                        return !isPath || getFilePathFromObjectPath(value);
                    }),
                };
            })
            .filter(({ properties }) => properties.length > 0);

        if (!groups.length) {
            dispatch(renderActions.setStamp(null));
        } else {
            setPropertyGroups(groups);
        }
    }, [show, stamp, starred, dispatch]);

    const handleSelectParent = async (id: ObjectId) => {
        if (!db || !view) {
            return;
        }
        const obj = await getObjectData({ db, id, view });
        if (!obj) {
            return;
        }

        dispatch(renderActions.setStamp(null));

        const handleId = performance.now();
        dispatch(renderActions.addLoadingHandle(handleId));
        try {
            // Use dummy abortController - not even sure this action should be cancellable
            const abortController = new AbortController();
            await selectObject({ obj, abortSignal: abortController.signal });
        } finally {
            dispatch(renderActions.removeLoadingHandle(handleId));
        }
    };

    if (!show) {
        return null;
    }

    return (
        <Box
            sx={{ pointerEvents: "auto" }}
            display={"flex"}
            flexDirection={"column"}
            flexGrow={1}
            maxHeight={340}
            minWidth={"min(calc(100vw - 32px), 500px)"}
            height={1}
        >
            <ScrollBox p={0}>
                {propertyGroups && propertyGroups.length > 0 && (
                    <PropertyList
                        propertyGroup={propertyGroups[0]}
                        propertyNameWidth={propertyNameWidth}
                        bindResizeHandlers={bindResizeHandlers}
                        onClose={() => dispatch(renderActions.setStamp(null))}
                    />
                )}

                {propertyGroups && propertyGroups.length > 1 && (
                    <Accordion
                        sx={{
                            [`&.${accordionClasses.root}.${accordionClasses.expanded}`]: {
                                marginBottom: 0,
                            },
                        }}
                    >
                        <AccordionSummary
                            sx={{
                                "&::after": {
                                    borderBottom: 0,
                                },
                            }}
                        >
                            {t("parentObjects")}
                            <InfoPopup
                                buttonProps={{ edge: "end", size: "small", sx: { ml: 1 } }}
                                popoverProps={{
                                    anchorOrigin: { vertical: "bottom", horizontal: "right" },
                                }}
                            >
                                <Box
                                    maxWidth="400px"
                                    p={1}
                                    dangerouslySetInnerHTML={{ __html: t("propertiesSelectParentInfo") }}
                                ></Box>
                            </InfoPopup>
                        </AccordionSummary>
                        <AccordionDetails>
                            {propertyGroups.slice(1).map((propertyGroup) => (
                                <PropertyList
                                    key={propertyGroup.objectId}
                                    propertyGroup={propertyGroup}
                                    propertyNameWidth={propertyNameWidth}
                                    bindResizeHandlers={bindResizeHandlers}
                                    onSelectParent={handleSelectParent}
                                />
                            ))}
                        </AccordionDetails>
                    </Accordion>
                )}
            </ScrollBox>
        </Box>
    );
}

function PropertyList({
    propertyGroup: { name, properties, objectId },
    propertyNameWidth,
    onClose,
    onSelectParent,
    bindResizeHandlers,
}: {
    propertyGroup: PropertyGroup;
    propertyNameWidth: number;
    onClose?: () => void;
    onSelectParent?: (objectId: ObjectId) => void;
    bindResizeHandlers: ReturnType<typeof useDrag>;
}) {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <>
            <Box px={1} display="flex" alignItems="center" justifyContent="space-between">
                <Typography fontWeight={600}>{name}</Typography>

                {onSelectParent && (
                    <Button onClick={() => onSelectParent(objectId)} size="small">
                        {t("select")}
                    </Button>
                )}
                {onClose && (
                    <IconButton size="small" onClick={onClose}>
                        <Close />
                    </IconButton>
                )}
            </Box>
            <Divider sx={{ mb: 0.5, borderColor: theme.palette.grey[200] }} />
            <List
                dense
                disablePadding
                sx={{ "& .propertyName": { width: propertyNameWidth } }}
                {...bindResizeHandlers()}
            >
                {properties.map(([key, val]) => {
                    const isPath = key.toLowerCase() === "path";
                    return (
                        <Box key={key} px={1} width={1} display="flex">
                            <Box className="propertyName" flexShrink={0} display="flex" justifyContent="space-between">
                                <Tooltip title={getPropertyDisplayName(key)}>
                                    <Typography noWrap={true}>
                                        {getPropertyDisplayName(key.split("/").at(-1) ?? key)}
                                    </Typography>
                                </Tooltip>
                                <ResizeHandle data-resize-handle>|</ResizeHandle>
                            </Box>
                            <Box flex="1 1 100%" width={0}>
                                <Tooltip title={isPath ? getFilePathFromObjectPath(val) : val}>
                                    <Typography noWrap={true}>
                                        {isPath ? (
                                            getFileNameFromPath(val)
                                        ) : isUrl(val) ? (
                                            <a href={val} target="_blank" rel="noreferrer">
                                                {val}
                                            </a>
                                        ) : (
                                            val
                                        )}
                                    </Typography>
                                </Tooltip>
                            </Box>
                        </Box>
                    );
                })}
            </List>
        </>
    );
}
